import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from '.';
import {showScroll} from '../app/utilities';
import { SelectOption } from '../models/SelectOption';
import { TaxonomyItem } from '../models/TaxonomyItem';
import { CategoryItem } from '../models/CategoryItem';
import { VideoInfo } from '../models/VideoInfo';

export interface SearchRequest {
  keyword: string,
  language: SelectOption,
  sortBy: SelectOption,
  scope: string,
  currentPage: number,
  category: string,
  taxonomy: TaxonomyItem[];
  pageSize: number;
  includeCaptions: boolean
}

export interface SearchResults {
  totalPages: number;
  totalVideos: number;
  languages: SelectOption[];
  categories: CategoryItem[];
  taxonomy: TaxonomyItem[];
  sortOrders: SelectOption[];
  videos: VideoInfo[];
  sortOrder: SelectOption;
  version: string;
}

export interface SearchState {
  loading: boolean,
  request: SearchRequest;
  results: SearchResults;
}

export const defaultSortOrder: SelectOption = {
  "text": "Newest",
  "value": "PublishedDate"
};

export const initialState: SearchState = {
  loading: false,
  request: {
    keyword: "",
    language: {
      "text": "ALL - Languages",
      "value": "ALL"
    },
    sortBy: defaultSortOrder,
    scope: "ALL",
    currentPage: 1,
    pageSize: 21,
    category: "",
    taxonomy: [],
    includeCaptions: true
  },
  results: {
    totalPages: 1,
    totalVideos: 0,
    languages: [],
    sortOrders: [],
    categories: [],
    taxonomy: [],
    videos: [],
    sortOrder: defaultSortOrder,
    version: ""
  }
}

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      showScroll(action.payload);
      state.loading = action.payload;
    },
    setKeyword: (state, action) => {
      state.request.keyword = action.payload;
    },
    setLanguage: (state, action) => {
      state.request.language = action.payload;
    },
    setSortOrder: (state, action) => {
      state.request.sortBy = action.payload;
    },
    setScope: (state, action) => {
      state.request.scope = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.request.currentPage = action.payload;
    },
    setCategory: (state, action) => {
      state.request.category = action.payload;
    },
    setIncludeCaptions: (state, action) => {
      state.request.includeCaptions = action.payload;
    },
    setRequest: (state, action) => {
      state.request = action.payload;
    },
    setTotalPages: (state, action) => {
      state.results.totalPages = action.payload;
    },
    setTotalVideos: (state, action) => {
      state.results.totalVideos = action.payload;
    },
    setLanguages: (state, action) => {
      state.results.languages = action.payload;
    },
    setSortOrders: (state, action) => {
      state.results.sortOrders = action.payload;
    },
    setCategories: (state, action) => {
      state.results.categories = action.payload;
    },
    setVideos: (state, action) => {
      state.results.videos = action.payload;
    },
    setResults: (state, action) => {
      state.request.sortBy = action.payload.sortOrder;
      state.request.taxonomy = action.payload.taxonomy;
      state.results = action.payload;
    },
    setPageSize: (state, action) => {
      state.request.pageSize = action.payload;
    },
    setTaxonomy: (state, action) => {
      state.request.taxonomy = action.payload;
    },
    changeCategory: (state, action) => {
      state.request.scope = action.payload.scope
      state.request.category = action.payload.category;
    }
  }
});

export const { setLoading, setKeyword, setLanguage, setSortOrder, setScope, setCurrentPage, setCategory, setRequest,
  setTotalPages, setTotalVideos, setLanguages, setSortOrders, setCategories, setVideos, setResults, setPageSize, changeCategory } = searchSlice.actions;

export const performSearch = (): AppThunk => async (dispatch, getState) => {
  let state = getState().search;
  let request = state?.request;
  let url = 'api/video/search';

  dispatch(setLoading(true));
  fetch(url, {
    method: "POST",
    body: JSON.stringify(request),
    headers: {
      "Content-Type": "application/json"
    },
    credentials: "same-origin"
  }).then(
    response => response.json()
  ).then(json => {
    dispatch(setResults(json));
    dispatch(setLoading(false));
  }).catch(err => {
    console.error(err);
    dispatch(setLoading(false));
  });
}

export default searchSlice.reducer;
